import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import ErrorBoundary from "./components/ErrorBoundary";
import AuthGuard from "./components/AuthGuard";
import DispatchGuard from "./components/DispatchGuard";
import PartnerGuard from "./components/PartnerGuard";
import RequireRole from "./components/RequireRole";

// Public & auth pages (eager - small, needed immediately)
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";

// Lazy-loaded shells
const CustomerShell = lazy(() => import("./components/layout/CustomerShell"));
const SupplierShell = lazy(() => import("./components/layout/SupplierShell"));
const PartnerShell = lazy(() => import("./components/layout/PartnerShell"));
const AdminShell = lazy(() => import("./components/layout/AdminShell"));
const DispatchShell = lazy(() => import("./components/layout/DispatchShell"));

// Lazy-loaded pages (backward compat flat routes)
const Order = lazy(() => import("./pages/Order"));
const VehicleSearch = lazy(() => import("./pages/VehicleSearch"));
const SupplierAuth = lazy(() => import("./pages/SupplierAuth"));
const SupplierLanding = lazy(() => import("./pages/SupplierLanding"));
const CustomerSignIn = lazy(() => import("./pages/CustomerSignIn"));
const CustomerSignUp = lazy(() => import("./pages/CustomerSignUp"));
const ProviderDashboard = lazy(() => import("./pages/ProviderDashboard"));
const ClientDashboard = lazy(() => import("./pages/ClientDashboard"));
const MyBookings = lazy(() => import("./pages/MyBookings"));
const Updates = lazy(() => import("./pages/Updates"));
const Specials = lazy(() => import("./pages/Specials"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const Profile = lazy(() => import("./pages/Profile"));
const VerifyMe = lazy(() => import("./pages/VerifyMe"));
const SupplierFleet = lazy(() => import("./pages/SupplierFleet"));
const SupplierProfile = lazy(() => import("./pages/SupplierProfile"));
const SupplierContracts = lazy(() => import("./pages/SupplierContracts"));
const SupplierBookings = lazy(() => import("./pages/SupplierBookings"));
const SupplierBilling = lazy(() => import("./pages/SupplierBilling"));
const SupplierBookingsInbox = lazy(() => import("./pages/SupplierBookingsInbox"));
const AddVehicle = lazy(() => import("./pages/AddVehicle"));
const FleetSchedule = lazy(() => import("./pages/FleetSchedule"));
const AdminAuth = lazy(() => import("./pages/AdminAuth"));
const SupplierInspection = lazy(() => import("./pages/SupplierInspection"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Install = lazy(() => import("./pages/Install"));
const EmailConfirmation = lazy(() => import("./pages/EmailConfirmation"));
const OrderReview = lazy(() => import("./pages/OrderReview"));
const ReliabilityScore = lazy(() => import("./pages/ReliabilityScore"));
const DispatchAdminDashboard = lazy(() => import("./pages/DispatchAdminDashboard"));
const FranchiseeAdminDashboard = lazy(() => import("./pages/FranchiseeAdminDashboard"));
const PartnerLanding = lazy(() => import("./pages/PartnerLanding"));
const PartnerApplication = lazy(() => import("./pages/PartnerApplication"));
const FranchiseeAuth = lazy(() => import("./pages/FranchiseeAuth"));
const HireContract = lazy(() => import("./pages/HireContract"));
const AppFlowcharts = lazy(() => import("./pages/AppFlowcharts"));
const RiskEngine = lazy(() => import("./pages/RiskEngine"));
const SupplierKYC = lazy(() => import("./pages/SupplierKYC"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));

const queryClient = new QueryClient();

const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen-safe">
    <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
  </div>
);

const LegacyAdminRedirect = () => {
  const location = useLocation();
  const nextPath = location.pathname.replace(/^\/a\b/, "/admin");
  return <Navigate to={`${nextPath}${location.search}${location.hash}`} replace />;
};

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthGuard />
          {/* Logo in shells serves as home button */}
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Public */}
              <Route path="/" element={<Index />} />
              <Route path="/about" element={<AboutUs />} />
              <Route path="/suppliers" element={<SupplierLanding />} />
              <Route path="/join" element={<SupplierLanding />} />
              <Route path="/operate" element={<PartnerLanding />} />
              <Route path="/partner-application" element={<PartnerApplication />} />
              <Route path="/install" element={<Install />} />
              <Route path="/flowcharts" element={<AppFlowcharts />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />

              {/* Auth pages — each route declared exactly once */}
              <Route path="/customer-signin" element={<CustomerSignIn />} />
              <Route path="/customer-signup" element={<CustomerSignUp />} />
              <Route path="/supplier-auth" element={<SupplierAuth />} />
              <Route path="/admin-auth" element={<AdminAuth />} />
              <Route path="/franchisee-login" element={<FranchiseeAuth />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/email-confirmation" element={<EmailConfirmation />} />

              {/* App Shells (lazy-loaded) */}
              <Route path="/c/*" element={<CustomerShell />} />
              <Route path="/s/*" element={<SupplierShell />} />
              <Route path="/p/*" element={<PartnerGuard><PartnerShell /></PartnerGuard>} />
              <Route
                path="/admin/*"
                element={
                  <RequireRole roles={["admin", "super_admin"]} fallbackTo="/admin-auth">
                    <AdminShell />
                  </RequireRole>
                }
              />
              <Route path="/a/*" element={<LegacyAdminRedirect />} />
              {/* Dispatch shell — guarded to dispatch/super admins only */}
              
              <Route path="/d/*" element={<DispatchGuard><DispatchShell /></DispatchGuard>} />
              <Route path="/dispatch/*" element={<DispatchGuard><DispatchShell /></DispatchGuard>} />

              {/* Backward compat: flat routes (no duplicates) */}
              <Route path="/vehicles" element={<VehicleSearch />} />
              <Route path="/order" element={<Order />} />
              <Route path="/provider-dashboard" element={<ProviderDashboard />} />
              <Route path="/dashboard" element={<ClientDashboard />} />
              <Route path="/my-bookings" element={<MyBookings />} />
              <Route path="/updates" element={<Updates />} />
              <Route path="/specials" element={<Specials />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/verify-me" element={<VerifyMe />} />
              <Route path="/reliability-score" element={<ReliabilityScore />} />
              <Route path="/supplier/fleet" element={<SupplierFleet />} />
              <Route path="/supplier/profile" element={<SupplierProfile />} />
              <Route path="/supplier/contracts" element={<SupplierContracts />} />
              <Route path="/supplier/bookings" element={<SupplierBookings />} />
              <Route path="/supplier/billing" element={<SupplierBilling />} />
              <Route path="/supplier/bookings-inbox" element={<SupplierBookingsInbox />} />
              <Route path="/supplier/add-vehicle" element={<AddVehicle />} />
              <Route path="/supplier/schedule" element={<FleetSchedule />} />
              <Route path="/supplier/inspection" element={<SupplierInspection />} />
              <Route path="/supplier/kyc" element={<SupplierKYC />} />
              <Route path="/franchisee-dashboard" element={<FranchiseeAdminDashboard />} />
              <Route path="/order-review" element={<OrderReview />} />
              <Route path="/hire-contract" element={<HireContract />} />

              {/* Catch-all */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
